import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./BrandVoice.css";
import { getUserId, getUserRole, getUserEmail } from "../../utils/tokenUtils";
import axios from "axios";
import API_URL from "../../apiConfig";
import UserProfileBox from "../userProfileBox/UserProfileBox"; // Adjust the import path
import { BrandService } from "../../services/brand/brandService";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import TopBar from "../../components/TopBar/TopBar";
import Loader from "../../components/common/Loader/Loader";
interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: "create" | "update";
  initialDetails?: string;
  id?: number;
}

const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  initialDetails = "",
  id,
}) => {
  const [details, setDetails] = useState(initialDetails);
  const [userId] = useState(getUserId());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ details?: string }>({});

  useEffect(() => {
    if (isOpen) {
      setDetails(initialDetails);
    }
  }, [isOpen, initialDetails]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const { showToast } = useToast();

  if (!isOpen) return null;

  const validContentRegex = /^[\s\S]*$/;

  const validate = () => {
    const newErrors: { details?: string } = {};

    if (!details.trim()) {
      newErrors.details = "Brand details are required.";
    } else if (!validContentRegex.test(details)) {
      newErrors.details =
        "Details must include at least one letter and only use letters, numbers, or basic punctuation.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) return;

    try {
      setIsSubmitting(true);

      const isCreateMode = mode === "create";
      const resp = isCreateMode
        ? await BrandService.createBrandVoice(details, userId)
        : await BrandService.updateBrandVoice(
            id as number,
            Number(userId),
            details.trim(),
            ""
          );

      if (resp.status >= 200 && resp.status < 300) {
        if (isCreateMode) showToast("Brand voice created!", "success");
        else showToast("Brand voice updated!", "success");

        onClose();
        onSuccess();
        setDetails("");

        // Step 2: Call external API
        try {
          const externalRes = await fetch(
            "https://tntoy2hx2b.execute-api.us-east-1.amazonaws.com/wingbot-bv-stage/",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: parseInt(userId),
                bv_name: "global", // fixed brand name
                bv_details: details,
              }),
            }
          );

          const externalData = await externalRes.json();

          if (externalRes.ok && externalData?.body) {
            const parsedBody = JSON.parse(externalData.body);

            if (parsedBody?.original_filename) {
              const idToUpdate = isCreateMode ? resp.data.data.id : id;
              await BrandService.updateBrandVoice(
                idToUpdate,
                parseInt(userId),
                details.trim(),
                parsedBody.original_filename
              );
            }
          } else {
          }
        } catch (externalErr) {
        }
      } else {
        alert(resp.data.message || "Something went wrong.");
      }
    } catch (error) {
      alert("Failed to save brand voice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="task-modal-overlay">
      <div className="task-modal-content">
        <div className="task-modal-header">
          <h2>Brand Guidelines</h2>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-modal-close-icon"
            onClick={onClose}
          />
        </div>
        <hr className="task-modal-divider" />
        <div className="task-modal-body">
          <label>
            Add details of your brand's identity{" "}
            <span className="task-modal-required">*</span>
          </label>
          <textarea
            placeholder="Enter all your brand communication guidelines here..."
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={2000}
            disabled={isSubmitting} // prevent typing during submit
          />
          <div className="task-modal-char-count">{details.length}/2000</div>
        </div>
        {errors.details && (
          <p className="error-message-deatils">{errors.details}</p>
        )}
        <div className="task-modal-footer">
          <button
            className="task-modal-cancel-btn"
            onClick={onClose}
            disabled={isSubmitting} // disable cancel while submitting
          >
            Cancel
          </button>
          <button
            className={`task-modal-confirm-btn ${
              isSubmitting ? "loading" : ""
            }`}
            disabled={isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting ? "Submitting..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedCategory: any | null;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedCategory,
}) => {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [userId, setUserId] = useState(getUserId() || "");
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const [showCategoryDeleteConfirmation, setShowCategoryDeleteConfirmation] =
    useState(false);
  // Validation state
  const [errors, setErrors] = useState<{ title?: string; details?: string }>(
    {}
  );

  useEffect(() => {
    if (isOpen) {
      if (selectedCategory) {
        setTitle(selectedCategory.category_title);
        setDetails(selectedCategory.category_details);
      } else {
        setTitle("");
        setDetails("");
      }
      setErrors({});
    }
  }, [isOpen, selectedCategory]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setTitle("");
    setDetails("");
    setErrors({});
    onClose();
  };

  const validate = () => {
    const newErrors: { title?: string; details?: string } = {};
    const validContentRegex = /^[\s\S]*$/;

    if (!title.trim()) {
      newErrors.title = "Category title is required.";
    } else if (!validContentRegex.test(title)) {
      newErrors.title =
        "Title must include at least one letter and only use letters, numbers, or basic punctuation.";
    }

    if (!details.trim()) {
      newErrors.details = "Category details are required.";
    } else if (!validContentRegex.test(details)) {
      newErrors.details =
        "Details must include at least one letter and only use letters, numbers, or basic punctuation.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) return;

    try {
      setIsLoading(true);

      const resp = selectedCategory
        ? await BrandService.updateBrandCategoryVoice(
            selectedCategory?.id || "",
            parseInt(userId),
            title,
            details,
            selectedCategory?.filename || ""
          )
        : await BrandService.createBrandCategoryVoice(
            selectedCategory ? selectedCategory.id : undefined,
            selectedCategory ? undefined : parseInt(userId),
            title,
            details,
            ""
          );

      if (resp.status >= 200 && resp.status < 300) {
        const categoryId = selectedCategory?.id || resp.data.data.id;

        try {
          const externalRes = await fetch(
            "https://tntoy2hx2b.execute-api.us-east-1.amazonaws.com/wingbot-bv-stage/",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: parseInt(userId),
                bv_name: title,
                bv_details: details,
              }),
            }
          );

          const externalData = await externalRes.json();
          const parsedBody = JSON.parse(externalData.body);

          if (externalRes.ok && parsedBody?.original_filename) {
            await BrandService.updateBrandCategoryVoice(
              categoryId,
              parseInt(userId),
              title,
              details,
              parsedBody.original_filename
            );
          }
        } catch (apiError) {
        }
      } else {
        setErrors({ title: resp.data.message || "Something went wrong." });
      }

      setTitle("");
      setDetails("");
      onClose();
      onSuccess();
    } catch (error) {
      setErrors({ title: "Failed to process category." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="category-modal-overlay">
      <div
        className="category-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="category-modal-header">
          <h2>
            {selectedCategory
              ? "Edit Category Guidelines"
              : "Create Category Guidelines"}
          </h2>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="category-modal-close-icon"
            onClick={!isLoading ? onClose : undefined}
            style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
          />
        </div>
        <hr className="category-modal-divider" />
        <div className="category-modal-body">
          <div className="category-modal-form-group">
            <label>
              Category Title <span className="category-modal-required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="category-modal-input"
              disabled={isLoading}
              maxLength={100}
            />
            <div className="category-modal-char-count">{title.length}/100</div>
            {errors.title && <p className="error-message">{errors.title}</p>}
          </div>
          <div className="category-modal-form-group">
            <label>
              Category Details{" "}
              <span className="category-modal-required">*</span>
            </label>
            <textarea
              placeholder="Enter all your category details here..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={2000}
              className="category-modal-textarea"
              disabled={isLoading}
            />
            <div className="category-modal-char-count">
              {details.length}/2000
            </div>
            {errors.details && (
              <p className="error-message-deatils">{errors.details}</p>
            )}
          </div>
        </div>
        <div className="category-modal-footer">
          <button
            className="category-modal-cancel-btn"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className={`category-modal-confirm-btn ${
              isLoading ? "loading" : ""
            }`}
            disabled={isLoading}
            onClick={handleConfirm}
          >
            {isLoading ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProfilePopupProps {
  onClose: () => void;
}

const ProfilePopup: React.FC<ProfilePopupProps> = ({ onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState(getUserId() || "6");

  useEffect(() => {
    setFirstName(localStorage.getItem("firstName") || "");
    setLastName(localStorage.getItem("lastName") || "");
    setProfileImage(localStorage.getItem("profileImage") || "");
    setRole(getUserRole() || "");
    setPhone(localStorage.getItem("phone") || "");
    setDob(localStorage.getItem("dob") || "");
    setEmail(getUserEmail() || "");
  }, []);

  return (
    <div className="popup-overlay">
      <div className="profile-popup">
        <div className="popup-header">
          <h3>My Profile</h3>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-icon"
            onClick={onClose}
          />
        </div>
        <hr className="divider" />

        <div className="popup-body">
          <div className="user-card">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="user-image" />
            ) : (
              <div className="user-placeholder">
                {firstName.charAt(0)}
                {lastName.charAt(0)}
              </div>
            )}

            <div className="user-info">
              <h4>
                {firstName} {lastName}
              </h4>
              <p className="user-role">{role || "Role Unknown"}</p>
            </div>

            <div className="action-buttons">
              <button className="btn change">Change Image</button>
              <button className="btn delete">Delete Image</button>
            </div>
          </div>

          <hr className="divider-two" />

          <div className="contact-info">
            <div className="info-item">
              <div className="info-content">
                <img src="/images/phone.svg" alt="Phone Icon" />
                <div className="info-text">
                  <span className="info-label">Phone:</span>
                  <span>{phone || "N/A"}</span>
                </div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-content">
                <img src="/images/dateofbirth.svg" alt="DOB Icon" />
                <div className="info-text">
                  <span className="info-label">Date of Birth:</span>
                  <span>{dob || "N/A"}</span>
                </div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-content">
                <img src="/images/email.svg" alt="Email Icon" />
                <div className="info-text">
                  <span className="info-label">Email:</span>
                  <span>{email || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
interface CompanyProfilePopupProps {
  onClose: () => void;
}

const CompanyProfilePopup: React.FC<CompanyProfilePopupProps> = ({
  onClose,
}) => {
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [location, setLocation] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [about, setAbout] = useState("");
  const [userId, setUserId] = useState(getUserId() || "6");

  useEffect(() => {
    // Replace with actual logic or API data
    setCompanyName("Joe’s Gear and Apparel");
    setCompanyLogo("/images/companylogo.png");
    setCompanyType("Retail company");
    setLocation("Pennsylvania, United States");
    setEmail("mail.joesgear@gmail.com");
    setPhone("+91 - 8978645789");
    setCompanyUrl("www.joesgearandapparel.com");
    setAbout(
      "Joe’s Gear and Apparel, founded in 1948, is a nationwide sporting goods retailer with a mission to help athletes achieve their goals and reach their full potential, offering a wide variety of sports equipment, apparel, and footwear, both online and in-store."
    );
  }, []);

  return (
    <div className="popup-overlay">
      <div className="profile-popup">
        <div className="popup-header">
          <h3>Company Profile</h3>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-icon"
            onClick={onClose}
          />
        </div>
        <hr className="divider" />

        <div className="popup-body company-popup">
          <div className="company-card">
            <div className="company-info">
              <img
                src="/images/companyimage.svg"
                alt="Company Logo"
                className="company-logo"
              />
              <h4>{companyName}</h4>
              <p className="company-type">{companyType}</p>
              <p className="company-location">{location}</p>
            </div>
          </div>

          <hr className="divider-two" />

          <div className="company-popup-wrapper">
            {/* Row layout for email & phone */}
            <div className="company-row-fields">
              <div className="company-item">
                <div className="company-icon-text">
                  <img src="/images/email.svg" alt="Email Icon" />
                  <div className="company-text-block">
                    <span className="company-label">Email:</span>
                    <span>{email}</span>
                  </div>
                </div>
              </div>

              <div className="company-item">
                <div className="company-icon-text">
                  <img src="/images/phone.svg" alt="Phone Icon" />
                  <div className="company-text-block">
                    <span className="company-label">Phone:</span>
                    <span>{phone}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Column layout for URL & About */}
            <div className="company-item">
              <div className="company-icon-text">
                <img src="/images/companyurl.svg" alt="Company URL Icon" />
                <div className="company-text-block">
                  <span className="company-label">Company URL:</span>
                  <span>{companyUrl}</span>
                </div>
              </div>
            </div>

            <div className="company-item">
              <div className="company-icon-text">
                <img src="/images/aboutcompany.svg" alt="About Icon" />
                <div className="company-text-block">
                  <span className="company-label">About Company:</span>
                  <p>{about}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
interface BrandVoiceProps {
  activeTab: string;
}

const BrandVoice: React.FC<BrandVoiceProps> = ({ activeTab }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [brandVoiceData, setBrandVoiceData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("Brand Guidelines");
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [editedDetails, setEditedDetails] = useState("");
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showCompanyProfilePopup, setShowCompanyProfilePopup] = useState(false);
  const [userId, setUserId] = useState(getUserId() || "6");
  const navigate = useNavigate();
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  useEffect(() => {
    setFirstName(localStorage.getItem("firstName") || "");
    setLastName(localStorage.getItem("lastName") || "");
    setProfileImage(localStorage.getItem("profileImage") || "");
    setRole(getUserRole() || "");
    setStatus(localStorage.getItem("status") || "");
  }, []);

  const toggleDetails = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const fetchBrandVoiceData = () => {
    setLoading(true);
    // axios
    //   .post(`${API_URL}/api/getBrandVoices`, { user_id: parseInt(userId) })
    BrandService.getBrandVoices(userId)
      .then((response) => {
        setBrandVoiceData(response.data);
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchBrandCategoryVoicesData = () => {
    setLoading(true);
    // axios
    //   .post(`${API_URL}/api/getAllBrandCategoryVoices`, { user_id: parseInt(userId) }) // Use POST for sending body data
    BrandService.getAllBrandCategoryVoices(userId)
      .then((response) => {
        setBrandVoiceData(response.data); // You can modify this according to the response structure
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedTab === "Brand Guidelines") {
      fetchBrandVoiceData();
    } else if (selectedTab === "Category Guidelines") {
      fetchBrandCategoryVoicesData();
    }
  }, [selectedTab]);
  const handleEdit = (category: any) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };
  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await BrandService.deleteBrandCategoryVoice(deleteId);
      fetchBrandCategoryVoicesData(); // Refresh after delete
    } catch (error) {
      alert("Failed to delete category");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };
  if (loading) return <main
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader inline={true} />
      </main>;

  return (
    <main className="content">
     <TopBar />

      <section className="brand-task-section">
        <h2 className="brand-voice-heading-tabs">
          {selectedTab === "Brand Guidelines"
            ? "Brand Guidelines"
            : "Category Guidelines"}
        </h2>
        <p className="brand-voice-description-tabs">
          Define your Voice & Tone Profile so Helen can craft product
          descriptions that reflect your brand’s unique language, style, and
          perspective.
        </p>

        <div className="brand-guidelines-task-tabs">
          {["Brand Guidelines", "Category Guidelines"].map((tab) => (
            <button
              key={tab}
              className={`brand-guidelines-tab-button ${
                selectedTab === tab ? "active-tab" : ""
              }`}
              onClick={() => setSelectedTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <section className="brand-tasks-list">
          {selectedTab === "Brand Guidelines" ? (
            <div className="brand-task-container">
              {brandVoiceData && brandVoiceData.data.length > 0 ? (
                <div className="brand-voice-details-box">
                  <p>
                    <strong>Your Brand's identity:</strong>
                  </p>
                  <div className="brand-voice-details-textarea">
                    <p>{brandVoiceData.data[0].brand_details}</p>
                  </div>
                  <button
                    className="edit-brandvoice"
                    onClick={() => {
                      setEditedDetails(brandVoiceData.data[0].brand_details);
                      setCurrentId(brandVoiceData.data[0].id); // Or whatever the ID field is named
                      setIsModalOpen(true);
                    }}
                  >
                    Edit details
                  </button>

                  <TaskModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={fetchBrandVoiceData}
                    mode="update"
                    id={currentId ?? undefined} // This ensures it's never null
                    initialDetails={editedDetails}
                  />
                </div>
              ) : (
                <div className="brand-voice-main">
                  <div className="brand-voice-row">
                    <div className="brand-voice-text">
                      <h3 className="brand-voice-heading">
                        Get Started by defining your brand communication
                        guidelines
                      </h3>
                      <p className="brand-voice-description">
                        The brand communication guideline is information, using
                        vivid descriptions, technical details, comparisons,
                        short paragraphs, bullet points, conversational tone,
                        and brand values that highlight product benefits.
                      </p>
                      <div className="task-buttons">
                        <button
                          onClick={() => setIsModalOpen(true)}
                          className="task-btn bulk-task-btn"
                        >
                          Add Brand Guidelines
                        </button>
                      </div>
                      <TaskModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSuccess={fetchBrandVoiceData}
                        mode="create"
                      />
                    </div>
                    <img
                      src="/images/speaker.svg"
                      alt="Speaker Icon"
                      className="brand-voice-icon"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="category-task-container">
              {brandVoiceData && brandVoiceData.data.length > 0 ? (
                <>
                  {brandVoiceData.data.map((category: any) => (
                    <div
                      key={category.id}
                      className={`brand-voice-details-box ${
                        expandedId === category.id ? "expanded" : ""
                      }`}
                    >
                      <div className="brand-voice-header">
                        <p className="brand-voice-title">
                          {category.category_title}
                        </p>
                        <div className="brand-voice-actions">
                          <img
                            src="/images/Edit.svg"
                            alt="Edit"
                            className="icon-image"
                            onClick={() => handleEdit(category)}
                            title="Edit"
                          />
                          <img
                            src="/images/Delete.svg"
                            alt="Delete"
                            className="icon-image"
                            onClick={() => handleDelete(category.id)}
                            title="Delete"
                          />
                          <button
                            className="brand-voice-toggle"
                            onClick={() => toggleDetails(category.id)}
                          >
                            {expandedId === category.id
                              ? "Hide Details"
                              : "Show Details"}
                            <img
                              src="/images/downarrow.svg"
                              alt="Arrow"
                              className="arrow-icon"
                            />
                          </button>
                        </div>
                      </div>

                      {expandedId === category.id && (
                        <div className="brand-voice-details-textarea">
                          <div className="details-label">
                            <p>Category Guidelines</p>
                          </div>
                          <div className="details-label-category-view">
                            <p>{category.category_details}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 🔽 Add Category button BELOW the list */}
                  <div
                    className="task-buttons"
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedCategory(undefined);
                        setIsCategoryModalOpen(true);
                      }}
                      className="task-btn bulk-task-btn"
                    >
                      + Add Category
                    </button>
                  </div>

                  {/* Add Category Modal */}
                  <CategoryModal
                    isOpen={isCategoryModalOpen}
                    onClose={() => setIsCategoryModalOpen(false)}
                    onSuccess={fetchBrandCategoryVoicesData}
                    selectedCategory={selectedCategory}
                  />
                </>
              ) : (
                <div className="brand-task-container">
                  <div className="brand-voice-main">
                    <div className="brand-voice-row">
                      <div className="brand-voice-text">
                        <h3 className="brand-voice-heading">Make a Category</h3>
                        <p className="brand-voice-description">
                          The brand category is a sub filter for Helen or a
                          Product Range you are launching under your parent
                          brand.
                        </p>
                        <div className="task-buttons">
                          <button
                            onClick={() => {
                              setSelectedCategory(null); // Reset to null for create
                              setIsCategoryModalOpen(true);
                            }}
                            className="task-btn bulk-task-btn"
                          >
                            Add Category Voice
                          </button>
                        </div>
                        <CategoryModal
                          isOpen={isCategoryModalOpen}
                          onClose={() => setIsCategoryModalOpen(false)}
                          onSuccess={fetchBrandCategoryVoicesData}
                          selectedCategory={selectedCategory}
                        />
                      </div>
                      <img
                        src="/images/speaker.svg"
                        alt="Speaker Icon"
                        className="brand-voice-icon"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </section>
      {showDeleteModal && (
        <ConfirmModal
          title=""
          message="Are you sure you want to delete this category?"
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </main>
  );
};

export default BrandVoice;
